import React from "react";
import { Form as BSForm } from "react-bootstrap";
import * as Yup from "yup";
import { ValidationSummary } from "../ValidationSummary/ValidationSummary";
import "./Form.scss";

export type FormState = {
    errorSummary: FormErrorSummary;
    handleSubmit: (e: any) => void;
};

export type FormErrorSummary = {
    values: any;
    errors: string[];
};

export type FormChildrenRenderProps = {
    (FormState: FormState): JSX.Element;
};

export interface FormProps {
    validationSchema?: Yup.Schema<any>;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    values?: any;
    showErrors?: boolean;
    name?: string;
    inline?: boolean;
    horizontal?: boolean;
    children?: FormChildrenRenderProps | React.ReactNode;
}

export const Form: React.FC<FormProps> = props => {
    let {
        validationSchema,
        onSubmit,
        values,
        children,
        showErrors,
        ...bsFormProps
    } = props;

    showErrors = props.showErrors == null ? true : props.showErrors;
    const [errorSummary, setErrorSummary] = React.useState<FormErrorSummary>({
        errors: [],
        values: {}
    });

    const validationErrorReducer = (
        obj: any,
        validationError: Yup.ValidationError
    ) => {
        obj[validationError.path] = validationError.message;
        return obj;
    };

    async function handleSubmit(e: React.FormEvent<any>) {
        e.preventDefault();

        if (validationSchema && values) {
            try {
                await validationSchema.validate(values, { abortEarly: false });
                setErrorSummary({ errors: [], values: {} });
            } catch (yupErrors) {
                const values = (yupErrors.inner as Yup.ValidationError[]).reduce(
                    validationErrorReducer,
                    {}
                );
                setErrorSummary({
                    values: values,
                    errors: yupErrors.errors
                });
                return;
            }
        }

        onSubmit(e);
    }

    let childContent: React.ReactNode;
    if (React.isValidElement(children)) {
        childContent = children;
    } else if (typeof children === "function") {
        childContent = (children as FormChildrenRenderProps)({
            handleSubmit,
            errorSummary
        });
    }

    return (
        <BSForm {...bsFormProps} onSubmit={handleSubmit}>
            {showErrors && <ValidationSummary errors={errorSummary.errors} />}
            {childContent}
        </BSForm>
    );
};

(Form as any).displayName = "Form";
